import { StorageProvider } from './StorageProvider';
import type { ChipsetSnapshot } from '../emulator/chipset/Chipset';

export interface SaveSlot {
  id: string;
  name: string;
  timestamp: number;
  modelId: string;
  screenshot?: Uint8Array;
}

function u8aReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Uint8Array) {
    return { __u8a: Array.from(value) };
  }
  return value;
}

function u8aReviver(_key: string, value: unknown): unknown {
  if (value && typeof value === 'object' && '__u8a' in (value as Record<string, unknown>)) {
    const arr = (value as { __u8a: number[] }).__u8a;
    if (Array.isArray(arr)) {
      return new Uint8Array(arr);
    }
  }
  return value;
}

function encode(value: unknown): Uint8Array {
  const json = JSON.stringify(value, u8aReplacer);
  return new TextEncoder().encode(json);
}

function decode<T>(data: Uint8Array): T {
  const json = new TextDecoder().decode(data);
  return JSON.parse(json, u8aReviver) as T;
}

const META_PREFIX = 'snapshot_meta:';
const SNAP_PREFIX = 'snapshot:';

function metaKey(slotId: string): string {
  return `${META_PREFIX}${slotId}`;
}

function snapKey(slotId: string): string {
  return `${SNAP_PREFIX}${slotId}`;
}

export class SaveSlotManager {
  constructor(private storage: StorageProvider) {}

  async listSlots(): Promise<SaveSlot[]> {
    const keys = await this.storage.list(META_PREFIX);
    const slots: SaveSlot[] = [];
    for (const key of keys) {
      const data = await this.storage.get(key);
      if (data) {
        slots.push(decode<SaveSlot>(data));
      }
    }
    return slots.sort((a, b) => b.timestamp - a.timestamp);
  }

  async saveSlot(
    slotId: string,
    name: string,
    snapshot: ChipsetSnapshot,
    modelId: string,
    screenshot?: Uint8Array,
  ): Promise<void> {
    const slot: SaveSlot = {
      id: slotId,
      name,
      timestamp: Date.now(),
      modelId,
      screenshot,
    };

    await this.storage.set(metaKey(slotId), encode(slot));
    await this.storage.set(snapKey(slotId), encode(snapshot));
  }

  async loadSlot(slotId: string): Promise<ChipsetSnapshot | null> {
    const data = await this.storage.get(snapKey(slotId));
    if (!data) return null;
    return decode<ChipsetSnapshot>(data);
  }

  async deleteSlot(slotId: string): Promise<void> {
    await this.storage.delete(metaKey(slotId));
    await this.storage.delete(snapKey(slotId));
  }
}
